import React, { useState, useEffect, useRef } from 'react';
import {
  Stack,
  Title,
  Group,
  Paper,
  Text,
  Badge,
  Progress,
  Grid,
  Card,
  Button,
  Alert,
  Loader,
  Center,
  Timeline,
  Switch,
  Box
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconVideo,
  IconLivePhoto,
  IconClock,
  IconLock,
  IconCheck,
  IconPlayerPlay,
  IconBell,
  IconBellOff,
  IconUser,
  IconSchool,
  IconCertificate, IconBrandWhatsapp
} from '@tabler/icons-react';
import { memberDashboard } from '../../services/memberDashboard';
import { useAuth } from '../../contexts/AuthContext';
import ClassViewer from '../../components/member/ClassViewer';
import BottomStickySection from '../../components/member/BottomStickySection';
import { useNavigate } from 'react-router-dom';


const MemberDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedClass, setSelectedClass] = useState(null);
  //const [waNotifications, setWaNotifications] = useState(true);
  const [showBottomSection, setShowBottomSection] = useState(true);
  const [currentClassIndex, setCurrentClassIndex] = useState(-1);
  const [nextClassIndex, setNextClassIndex] = useState(-1);
  const [inClassView, setInClassView] = useState(false);
  const { user, checkAuth } = useAuth();

  const [hasInitiatedMarkingAllLive, setHasInitiatedMarkingAllLive] = useState(false);

  const [settingAllLiveAsCompleted, SetAllLiveAsCompleted] = useState(false);

  const classRefs = useRef([]);
  const timelineRef = useRef(null);

  const [waNotifications, setWaNotifications] = useState(false);
  const [initwaNotifications, setInitWaNotifications] = useState(false);
  const navigate = useNavigate();



  useEffect(() => {
    loadDashboard();
  }, []);

  useEffect(() => {
    if (user) {
      //console.log('User data:', user);
      if (!initwaNotifications) {
        setInitWaNotifications(true);
        setWaNotifications(!user.has_disabled_wa_notifications);
      }
    }
  }, [user]);

  useEffect(() => {
    if (data?.classes) {
      // Find current class (first available or in progress)
      const currentIndex = data.classes.findIndex(
        c => c.status === 'available' || c.status === 'in_progress'
      );
      setCurrentClassIndex(currentIndex);

      // Find next class (first coming_soon after current)
      const nextIndex = data.classes.findIndex(
        (c, index) => index > currentIndex && c.status === 'coming_soon'
      );
      setNextClassIndex(nextIndex);
    }
  }, [data]);

  useEffect(() => {
    // Handle scroll to show/hide bottom section
    const handleScroll = () => {
      if (currentClassIndex === -1) return;

      const currentClassElement = classRefs.current[currentClassIndex];
      if (!currentClassElement) return;

      const rect = currentClassElement.getBoundingClientRect();
      const isCurrentClassVisible = rect.top < window.innerHeight && rect.bottom > 0;

      // Show bottom section if current class is not visible and batch has started
      const batchStarted = data?.batch && new Date(data.batch.batch_start_time) <= new Date();
      setShowBottomSection(!isCurrentClassVisible && batchStarted);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [currentClassIndex, data]);



  // Define this function somewhere in your component
  const markLiveClassAsCompleted = async (live_class_id) => {
    if (settingAllLiveAsCompleted) return; // Prevent multiple simultaneous calls

    SetAllLiveAsCompleted(true);

    notifications.show({
      title: 'Processing',
      message: 'Marking Live as completed...',
      color: 'yellow',
    });

    try {
      // Step 1: Load class first
      await memberDashboard.getClass(live_class_id);

      // Step 2: Mark as completed
      await memberDashboard.markCompleted(live_class_id);

      notifications.show({
        title: 'Success',
        message: 'Reloading dashboard...',
        color: 'green',
      });

    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.message || 'Failed to mark class as complete',
        color: 'red',
      });
    } finally {
      // Step 3: Refresh dashboard view
      //await loadDashboard();
      window.location.reload();
      SetAllLiveAsCompleted(false);
    }
  };


  useEffect(() => {
    if (data?.batch?.finish_all_live && !hasInitiatedMarkingAllLive && data.classes) {
      console.log('Batch has finished all live classes, marking all live classes as completed');
      setHasInitiatedMarkingAllLive(true);

      data.classes.forEach(classItem => {
        if (classItem.class.type === 'live_class' && classItem.status !== 'completed') {
          markLiveClassAsCompleted(classItem.class.id);
        }
      });
    }
  }, [data?.batch?.finish_all_live, hasInitiatedMarkingAllLive, data?.classes]);



  const loadDashboard = async () => {
    try {
      const dashboardData = await memberDashboard.getDashboard();
      setData(dashboardData);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to load dashboard',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClassClick = (classItem) => {
    if (classItem.status === 'available' || classItem.status === 'in_progress' || classItem.status === 'completed') {
      setSelectedClass(classItem);
    }
  };

  /*   const handleWaNotificationToggle = async (enabled) => {
      try {
        await memberDashboard.updateProfile({
          full_name: user.full_name,
          has_disabled_wa_notifications: !enabled,
        });
        console.log('handleWaNotificationToggle enabled :: ', enabled);
        setWaNotifications(enabled);
        checkAuth(); // Refresh user data
  
        notifications.show({
          title: 'Success',
          message: `WhatsApp notifications ${enabled ? 'enabled' : 'disabled'}`,
          color: 'green',
        });
      } catch (error) {
        notifications.show({
          title: 'Error',
          message: 'Failed to update notification settings',
          color: 'red',
        });
      }
    }; */

  const handleWaNotificationToggle = async (enabled) => {
    try {
      await memberDashboard.updateProfile({
        full_name: user.full_name,
        has_disabled_wa_notifications: !enabled,
      });

      setWaNotifications(enabled);
      checkAuth(); // This will now fetch fresh data from /member/me

      notifications.show({
        title: 'Success',
        message: `WhatsApp notifications ${enabled ? 'enabled' : 'disabled'}`,
        color: 'green',
      });
    } catch (error) {
      // error handling
    }
  };

  const handleGoToCurrentClass = () => {
    if (currentClassIndex === -1) return;

    const currentClassElement = classRefs.current[currentClassIndex];
    if (currentClassElement) {
      currentClassElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });

      // Add blinking animation
      currentClassElement.style.animation = 'pulse 1s ease-in-out 3';
      setTimeout(() => {
        if (currentClassElement) {
          currentClassElement.style.animation = '';
        }
      }, 3000);
    }
  };

  const handleCloseClassViewer = () => {
    setSelectedClass(null);
    // Override back button behavior by refreshing data
    loadDashboard();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'green';
      case 'in_progress': return 'blue';
      case 'available': return 'purple';
      case 'locked': return 'red';
      case 'coming_soon': return 'gray';
      default: return 'gray';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed': return 'Completed';
      case 'in_progress': return 'In Progress';
      case 'available': return 'Available';
      case 'locked': return 'Locked';
      case 'coming_soon': return 'Coming Soon';
      default: return 'Unknown';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <IconCheck size={16} />;
      case 'in_progress': return <IconPlayerPlay size={16} />;
      case 'available': return <IconPlayerPlay size={16} />;
      case 'locked': return <IconLock size={16} />;
      case 'coming_soon': return <IconClock size={16} />;
      default: return <IconClock size={16} />;
    }
  };

  const formatTimeUntilAvailable = (availableAt) => {
    const now = new Date();
    const available = new Date(availableAt);
    const diff = available - now;

    if (diff <= 0) return 'Available now';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) return `Available in ${days}d ${hours}h`;
    if (hours > 0) return `Available in ${hours}h ${minutes}m`;
    return `Available in ${minutes}m`;
  };

  if (loading) {
    return (
      <Center h={400}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (!data.batch || !data.curriculum) {
    return (
      <Center h={400}>
        <Stack align="center">
          <IconSchool size={64} color="gray" />
          <Title order={3} c="dimmed">No Course Assigned</Title>
          <Text c="dimmed" ta="center">
            You haven't been assigned to a batch yet. Please contact support.
          </Text>
        </Stack>
      </Center>
    );
  }

  // Check if batch start time is more than 1 year in future
  const batchStartTime = new Date(data.batch.batch_start_time);
  const oneYearFromNow = new Date();
  oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);

  if (batchStartTime > oneYearFromNow) {
    return (
      <Center h={400}>
        <Stack align="center">
          <IconSchool size={64} color="blue" />
          <Title order={3} c="blue">Coming Soon!</Title>
          <Text c="dimmed" ta="center" maw={400}>
            Start date will be released soon. You will get notified on WhatsApp when your batch is ready to begin.
          </Text>
          <Badge color="blue" size="lg">
            Batch #{data.batch.batch_number}
          </Badge>
        </Stack>
      </Center>
    );
  }



  // Check if batch hasn't started yet
  const batchHasStarted = new Date() >= batchStartTime;
  //console.log(data)

  return (
    <Box pb={batchHasStarted ? 0 : 120}>



      <Stack gap="md">
        {/* Header - Mobile Optimized */}
        <Paper withBorder p="md">
          <Stack gap="xs">
            <Group justify="space-between">
              <Stack gap="xs" style={{ flex: 1 }}>
                <Title order={2} size="h3" style={{ marginBottom: '-20px' }}>Hello, {data.member.full_name}</Title>
                {/* <Group wrap="wrap">
                  <Badge color="blue" leftSection={<IconSchool size={12} />}>
                    Batch #{data.batch.batch_number}
                  </Badge>
                </Group> */}


                <Group mt="xs">
                  <Text size="sm" c="dimmed" ta="left">
                    <IconBrandWhatsapp style={{ marginBottom: '-5px' }} size={20} color="#25D366" />
                    &nbsp;Join WhatsApp Community for Live Session Details and Class Updates and Delays.
                  </Text>
                </Group>

                {data.whatsapp_join_link && (
                  <Button
                    component="a"
                    href={data.whatsapp_join_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    leftSection={<IconBrandWhatsapp size={16} />}
                    variant="gradient"
                    gradient={{ from: '#25D366', to: '#128C7E', deg: 45 }}
                    size="sm"
                    radius="lg"
                    style={{
                      boxShadow: '0 4px 12px rgba(37, 211, 102, 0.3)',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    Join WhatsApp Community
                  </Button>
                )}



              </Stack>
            </Group>

          </Stack>
        </Paper>
        {/* <Paper withBorder p="md">
           <Stack gap="xs">
           
            <Group justify="normal" align="center">

              <Title order={2} size="h5">WhatsApp Notifications</Title>
              <Switch
                checked={waNotifications}
                onChange={(event) => handleWaNotificationToggle(event.currentTarget.checked)}
                onLabel={<IconBell size={12} />}
                offLabel={<IconBellOff size={12} />}
              />

            </Group>
            <Text size="xs" c="dimmed">
              {waNotifications ? (<Badge
                color={'green'}
                leftSection={<IconBell size={12} />}
                size="sm"
              >
                WhatsApp notifications enabled
              </Badge>) : (
                <Badge
                  color={'red'}
                  leftSection={<IconBellOff size={12} />}
                  size="sm"
                >
                  ALERT: WhatsApp notifications disabled
                </Badge>
              )}
            </Text>

          </Stack><br /> 
          {data.whatsapp_join_link && (
            <Button
              component="a"
              href={data.whatsapp_join_link}
              target="_blank"
              rel="noopener noreferrer"
              leftSection={<IconBrandWhatsapp size={16} />}
              variant="gradient"
              gradient={{ from: '#25D366', to: '#128C7E', deg: 45 }}
              size="sm"
              radius="lg"
              style={{
                boxShadow: '0 4px 12px rgba(37, 211, 102, 0.3)',
                transition: 'all 0.2s ease'
              }}
            >
              Join Batch {data.batch.batch_number} WhatsApp Community
            </Button>
          )}
        </Paper>*/}

        {/* Progress Overview - Mobile Optimized */}
        <Paper withBorder p="md">
          <Stack gap="sm">
            <Title order={3} size="h4">Course Progress</Title>
            <Progress
              value={data.progress_summary.completion_percentage}
              size="xl"
              radius="xl"
              label={`${data.progress_summary.completion_percentage}%`}
            />
            <Group justify="space-between">
              <Text size="sm">
                {data.progress_summary.completed_classes} of {data.progress_summary.total_classes} completed
              </Text>
              <Text size="sm" c="dimmed">
                {data.progress_summary.available_classes} available
              </Text>
            </Group>
          </Stack>
          {/* {data.progress_summary.completion_percentage === 100 ? (
            <Stack align="center" mt="md" gap="xs">
              <Text size="sm" c="dimmed" ta="center">
                You can now download your certificate
              </Text>
              <Button
                leftSection={<IconCertificate size={16} />}
                variant="gradient"
                gradient={{ from: 'blue', to: 'cyan', deg: 45 }}
                size="md"
                radius="lg"
                onClick={() => navigate('/member/certificate')}
                style={{
                  boxShadow: '0 4px 12px rgba(0, 123, 255, 0.3)',
                  transition: 'all 0.2s ease'
                }}
              >
                Download Certificate
              </Button>
            </Stack>
          ) : (<Text size="sm" c="dimmed" ta="center">
            Complete all classes to unlock your certificate.
          </Text>)} */}

        </Paper>


        {/* Class Timeline - Only show if batch has started */}
        {(
          <Paper withBorder p="md" ref={timelineRef}>
            <Stack gap="md">
              <Title order={3} size="h4">Class Schedule</Title>

              {data.classes.length === 0 ? (
                /* warn the user that their 30 days is over and they cannot access this content. to access again they have to purchase this course again. if this is an error then they can contact support  */
                <Alert color="red" title="Access Expired - 30 days access is complete" p="md">
                  <Text size="sm">
                    Your access to this course has expired. You can no longer view the content. If you believe this is an error, please contact support.
                  </Text>
                </Alert>
              ) : (
                /* 
                      case 'completed': return 'Completed';
                      case 'in_progress': return 'In Progress';
                      case 'available': return 'Available';
                      case 'locked': return 'Locked';
                      case 'coming_soon': return 'Coming Soon';
                      default: return 'Unknown';
                */
                <><Timeline active={data.classes.findLastIndex(c => c.status === 'completed' || c.status === 'available' || c.status === 'in_progress')} bulletSize={24} lineWidth={2}>
                  {data.classes.map((classItem, index) => (
                    <Timeline.Item
                      key={classItem.class.id}
                      ref={el => classRefs.current[index] = el}
                      bullet={
                        classItem.class.type === 'video_class' ? (
                          <IconVideo size={16} />
                        ) : (
                          <IconLivePhoto size={16} />
                        )
                      }
                      title={
                        <Group>
                          <Text fw={600} size="sm">{classItem.class.title}</Text>
                          <Badge
                            color={getStatusColor(classItem.status)}
                            leftSection={getStatusIcon(classItem.status)}
                            size="sm"
                          >

                            {getStatusText(classItem.status)}
                          </Badge>
                        </Group>
                      }
                    >
                      <Stack gap="xs">
                        {/* <Text size="sm" c="dimmed">
                          {classItem.class.short_description}
                        </Text> */}

                        <Group>
                          <Badge
                            color={classItem.class.type === 'video_class' ? 'blue' : 'green'}
                            variant="light"
                            size="xs"
                          >
                            {classItem.class.type === 'video_class' ? 'Video Class' : 'Live Class'}
                          </Badge>

                          <Text size="xs" c="dimmed">
                            {classItem.status === 'coming_soon'
                              ? formatTimeUntilAvailable(classItem.available_at)
                              : `Available: ${new Date(classItem.available_at).toLocaleString('en-IN')}`
                            }
                          </Text>
                        </Group>

                        {classItem.status === 'locked' && (
                          <Alert color="orange" p="xs">
                            <Text size="xs">
                              Complete the previous class quiz to unlock this class
                            </Text>
                          </Alert>
                        )}

                        {classItem.progress && classItem.progress.quiz_score && (
                          <Text size="xs" c="green">
                            Quiz Score: {classItem.progress.quiz_score}%
                            {classItem.progress.quiz_passed ? ' (Passed)' : ' (Failed)'}
                          </Text>
                        )}

                        {(classItem.status === 'available' || classItem.status === 'in_progress' || classItem.status === 'completed') &&
                          !(classItem.class.type === 'live_class' && data.batch.finish_all_live) && (
                            <Button
                              size="xs"
                              variant="light"
                              onClick={() => handleClassClick(classItem)}
                            >
                              {(classItem.class.type === 'live_class' && (classItem.status === 'completed' ? 'Live marked as completed' : (data.batch.finish_all_live ? 'Live Finished Click here to mark as finish' : 'Join Live Now')))}
                              {(classItem.class.type !== 'live_class' && (classItem.status === 'completed' ? 'Review Class' : 'Start Class'))}

                            </Button>
                          )}
                      </Stack>
                    </Timeline.Item>
                  ))}
                </Timeline>


                  <Paper withBorder p="md" mt="lg" radius="md">
                    <Stack gap="sm" align="center">
                      {/* <Stack gap="xs" align="center">
                        <Text fw={600} size="lg">Day 21. Celebrate & Anchor Your Manifestation Journey</Text>
                        <Text size="sm" c="dimmed" ta="center">
                          Reflect, celebrate, and lock in the frequency of your next level self.
                        </Text>
                      </Stack> */}

                      <Group mt="xs">
                        <Text size="sm" c="dimmed" ta="center">
                          <IconBrandWhatsapp size={20} color="#25D366" />
                          &nbsp;Join WhatsApp Community for Live Session Details and Class Updates and Delays.
                        </Text>
                      </Group>

                      {data.whatsapp_join_link && (
                        <Button
                          component="a"
                          href={data.whatsapp_join_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          leftSection={<IconBrandWhatsapp size={16} />}
                          variant="gradient"
                          gradient={{ from: '#25D366', to: '#128C7E', deg: 45 }}
                          size="sm"
                          radius="lg"
                          style={{
                            boxShadow: '0 4px 12px rgba(37, 211, 102, 0.3)',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          Join WhatsApp Community
                        </Button>
                      )}
                    </Stack>
                  </Paper>


                </>

              )}


            </Stack>
          </Paper>
        )}






        {/* Batch Information */}
        <Paper withBorder p="md">
          <Stack gap="sm">
            <Title order={4} size="h5">Batch Information</Title>
            <Grid>
              <Grid.Col span={12}>
                <Text size="sm" fw={500}>Batch Start Time:</Text>
                <Text size="sm" c="dimmed">
                  {new Date(data.batch.batch_start_time).toLocaleString('en-IN')}
                </Text>
              </Grid.Col>
              {/*  <Grid.Col span={12}>
                <Text size="sm" fw={500}>Enrollment Cap Time:</Text>
                <Text size="sm" c="dimmed">
                  {new Date(data.batch.cap_time).toLocaleString('en-IN')}
                </Text>
              </Grid.Col> */}
            </Grid>
          </Stack>
        </Paper>
      </Stack>

      {/* Bottom Sticky Section */}
      <BottomStickySection
        batch={data.batch}
        currentClass={currentClassIndex >= 0 ? data.classes[currentClassIndex] : null}
        nextClass={nextClassIndex >= 0 ? data.classes[nextClassIndex] : null}
        onGoToCurrentClass={handleGoToCurrentClass}
        isVisible={!batchHasStarted || showBottomSection}
        inClassView={!!selectedClass}
      />

      {/* Class Viewer Modal */}
      {selectedClass && (
        <ClassViewer
          classItem={selectedClass}
          onClose={handleCloseClassViewer}
          onProgress={loadDashboard}
        />
      )}
      <br /><br /><br /><br /><br /><br /><br /><br />
      <br /><br /><br /><br /><br /><br /><br /><br />
    </Box>
  );
};

export default MemberDashboard;